import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useStore } from 'react-redux'
import camelCase from 'camelcase'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ContentStateInterface,
  ContentStateProperty,
  InsertionEditorElements,
  InsertionEditorStateInterface,
  isEditEntryMode,
  OnChangeValue,
  SaveType,
  Screen,
  ScreenProperty,
} from '../../../@types/types'
import {
  SCREEN_ADDITIONAL_INFORMATION_CTA,
  SCREEN_ADDRESS,
  SCREEN_MULTIMEDIA,
  SCREEN_TENANT_DOCUMENTS,
  SCREENS,
} from '../../../services/domain/wizardScreens'
import { trackingPropertyMapping, TrackingPropertyMappingType } from '../../../tracking/tracking'

import { InitState } from '../../../redux/reducers/root/initialState'
import { doNavigate, doTracking, initInsertionEditor } from '../../../redux/thunks/wizard/wizard'
import { doSave as doSaveThunk } from '../../../redux/thunks/wizard/doSave'
import { checkValidity } from '../../../services/validation/validity/checkValidity'
import { doHideMessage } from '../../../redux/thunks/message/message'
import { getInvalidFields } from '../../../services/validation/fields/getInvalidFields'
import { convertTrackingParameter } from '../../../services/conversion/conversionServices'
import { getFlowScreens } from '../../../services/screen/screenService'
import { InsertionEditorProps } from '../../../@types/props-types'
import { FlowName } from '../../../@types/flow-types'
import { hideMultimediaMessage } from '../../../redux/reducers/message/messageFeature'
import { doPreview } from '../../../redux/thunks/wizard/doPreview'
import PreviewModalContainer from '../../common/display/previewModalContainer/PreviewModalContainer'
import PreviewModal from '../../common/display/previewModal/PreviewModal'
import { hidePreviewModal } from '../../../redux/reducers/preview/previewFeature'
import { useUpdateLocalStateWhenSuggestionsLoaded } from './UseUpdateLocalStateWhenSuggestionsLoaded'
import { useRetryAutosaveAfterSaveCompletion } from './useRetryAutosaveAfterSaveCompletion'
import { getElementProps } from './insertionEditorUtils'
import InsertionWizardPage from './wizard/InsertionWizardPage'
import { useFormState } from './wizard/useFormState'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { getDefaultLogger } from '../../../services/logger/logger'
import { validateAndSaveInsertion } from '../../../redux/thunks/insertionData/insertionData'
import _ from 'lodash'
import { insertionDataWithChangedProperty } from './insertionEditorValidators/CustomModifiers'
import { runValidation } from '../../../services/validation/editor/runValidation'
import { useApplyInsertionDataUpdate } from '../../logic/useApplyInsertionDataUpdate'
import { useFeaturesContext } from '../../../utils/features/FeatureSwitchContext'
import { useToastContext } from '../../../app/toast/toastContext'
import styles from './InsertionEditor.module.css'
import { isWebView } from '../../../utils/navigation'
import {
  getPropertyOwnerApartmentRentPrefill,
  hasCompletePrefilledAddress,
} from '../../../app/flows/rent/propertyOwner/apartment/propertyOwnerApartmentRentPrefill'
import { queryGeoCodes } from '../../../redux/thunks/gisService/gisService'
import { InsertionStartInfoSection } from './wizard/insertionStartInfoSection/InsertionStartInfoSection'
import { handleDeferredRedirectClose } from './deferredRedirect'

const initialState = (): InsertionEditorStateInterface => {
  return {
    currentScreen: SCREENS[SCREEN_ADDRESS],
    currentScreenIndex: 0,
  }
}

const hasText = (value: string | undefined) => !!value && value.trim().length > 0

const hasFilledValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return hasText(value)
  }
  if (typeof value === 'number') {
    return !Number.isNaN(value)
  }
  return value !== undefined && value !== null
}

const hasDraftListingMandatoryFields = (insertionData: ContentStateInterface): boolean =>
  hasText(insertionData.street) &&
  hasText(insertionData.houseNumber) &&
  hasText(insertionData.postcode) &&
  hasText(insertionData.city) &&
  hasFilledValue(insertionData.livingSpace) &&
  hasFilledValue(insertionData.room) &&
  hasFilledValue(insertionData.price)

const DRAFT_LISTING_TOAST_FLOW_NAMES: FlowName[] = [
  FlowName.PROPERTY_OWNER_APARTMENT_RENT,
  FlowName.PROPERTY_OWNER_HOUSE_RENT,
]
const WEB_VIEW_DRAFT_LISTING_INFO_FLOW_NAMES: FlowName[] = [
  FlowName.PROPERTY_OWNER_APARTMENT_RENT,
  FlowName.PROPERTY_OWNER_HOUSE_RENT,
  FlowName.PROPERTY_OWNER_APARTMENT_SELL,
  FlowName.PROPERTY_OWNER_HOUSE_SELL,
  FlowName.PROPERTY_OWNER_SHORT_TERM_ACCOMMODATION_RENT,
  FlowName.TENANT_APARTMENT_RENT,
  FlowName.TENANT_HOUSE_RENT,
  FlowName.TENANT_SHORT_TERM_ACCOMMODATION_RENT,
]
const DRAFT_PROGRESS_STATES = new Set(['DRAFT', 'NEW', 'IN_OFFER_API_NEW'])
const SCREEN_TO_SCROLL_SEARCH_PARAM = 'screenToScroll'

export type ListingParams = {
  // draft listing id which is available temporarily
  // if you want to operate on an existing listing, use realEstateId
  id: string
}

function InsertionEditor({
  flow: { flowScreens, flowInformation, defaultStateValues },
  objectParams,
}: InsertionEditorProps) {
  const { t } = useTranslation()
  const { id } = useParams<ListingParams>()
  const { addToast } = useToastContext()
  const { insertionData, validationResult, customValidationResults } = useAppSelector(
    (state: InitState) => state.insertionFeature
  )
  const realEstateId = useAppSelector((state: InitState) => state.insertionFeature.insertionIdentification.realEstateId)
  const progressState = useAppSelector(
    (state: InitState) => state.insertionFeature.insertionIdentification.progressState
  )
  const saveStatus = useAppSelector((state: InitState) => state.insertionFeature.save)
  const entryMode = useAppSelector(
    (state: InitState) => state.insertionFeature.insertionIdentification.metaInformation?.entryMode
  )
  const previewModalIsOpen = useAppSelector((state: InitState) => state.previewFeature.previewModalIsOpen)
  const store = useStore<InitState>()

  const [localState, setLocalState] = useState<InsertionEditorStateInterface>(() => initialState())
  const [isLoading, setIsLoading] = useState(true)
  const [isInitializationAborted, setIsInitializationAborted] = useState(false)
  const [deferredRedirectUrl, setDeferredRedirectUrl] = useState<string | undefined>(undefined)
  const dispatch = useAppDispatch()
  const { resetTouched } = useFormState()
  const previousSaveStatus = useRef(saveStatus)
  const lastDraftToastSignatureRef = useRef<string | undefined>(undefined)
  const hasDeferredRedirectedRef = useRef(false)
  const hasHandledScreenParamRef = useRef(false)
  const insertionPrefill = useMemo(
    () => getPropertyOwnerApartmentRentPrefill(new URLSearchParams(window.location.search), flowInformation.name, id),
    [flowInformation.name, id]
  )
  const initialStateValues = useMemo(
    () => ({ ...defaultStateValues, ...insertionPrefill }),
    [defaultStateValues, insertionPrefill]
  )

  const isTenantDocumentsScreenVisible = isEditEntryMode(entryMode)
  const isNativeMobileView = isWebView()
  const shouldShowWebViewDraftListingInfo =
    isNativeMobileView && WEB_VIEW_DRAFT_LISTING_INFO_FLOW_NAMES.includes(flowInformation.name)

  const featuresContext = useFeaturesContext()
  const isDraftListingToastFlow = DRAFT_LISTING_TOAST_FLOW_NAMES.includes(flowInformation.name)
  let wizardScreens = flowScreens.filter(
    (screen) => screen.feature === undefined || featuresContext.isActive(screen.feature)
  )
  wizardScreens = isTenantDocumentsScreenVisible
    ? wizardScreens
    : wizardScreens.filter((screen) => screen.screen !== SCREENS[SCREEN_TENANT_DOCUMENTS])

  const collapsedScreens = useAppSelector((state: InitState) => state.formStateFeature.collapsedScreens)

  wizardScreens = wizardScreens.filter((screen) => {
    const screenName = screen.screen.name as keyof typeof collapsedScreens
    return !collapsedScreens[screenName]
  })

  useEffect(() => {
    const dispatchInitInsertionEditor = async () => {
      try {
        getDefaultLogger().info('initInsertionEditor')
        const shouldAbortInitialization = await dispatch(
          initInsertionEditor(flowInformation, initialStateValues, id, objectParams)
        )
        setIsInitializationAborted(shouldAbortInitialization === true)
        if (!shouldAbortInitialization && hasCompletePrefilledAddress(insertionPrefill)) {
          await dispatch(queryGeoCodes())
        }
      } catch (e) {
        getDefaultLogger().warn('dispatch for init InsertionData does not work', e)
      } finally {
        setIsLoading(false)
      }
    }

    dispatchInitInsertionEditor()
    history.scrollRestoration = 'manual'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useUpdateLocalStateWhenSuggestionsLoaded()
  useRetryAutosaveAfterSaveCompletion(flowInformation.validationSchema)
  useEffect(() => {
    if (!deferredRedirectUrl) {
      return
    }

    addToast({
      headline: t('message.editListing.toast.headline'),
      message: t('message.editListing.toast.message'),
      type: 'success',
      timeout: 3200,
      variant: 'elevated',
      onClose: () =>
        handleDeferredRedirectClose(
          deferredRedirectUrl,
          hasDeferredRedirectedRef,
          () => setDeferredRedirectUrl(undefined),
          (redirectUrl) => {
            window.location.href = redirectUrl
          }
        ),
    })
  }, [addToast, deferredRedirectUrl, t])

  useEffect(() => {
    const hasSaveJustFinished = previousSaveStatus.current === SaveType.SAVING && saveStatus === SaveType.SAVED
    const hasDraftIdentifier = realEstateId !== undefined || !!id
    const isDraftProgressState = !progressState || DRAFT_PROGRESS_STATES.has(progressState)
    const isNotPublishedListing = insertionData.published !== true
    const draftToastSignature = JSON.stringify(insertionData)

    const shouldShowDraftListingToast =
      hasSaveJustFinished &&
      hasDraftIdentifier &&
      isDraftProgressState &&
      isNotPublishedListing &&
      lastDraftToastSignatureRef.current !== draftToastSignature &&
      isDraftListingToastFlow &&
      hasDraftListingMandatoryFields(insertionData)

    if (shouldShowDraftListingToast) {
      addToast({
        headline: t('message.draftListing.toast.headline'),
        message: t('message.draftListing.toast.message'),
        type: 'success',
        timeout: 6000,
        variant: 'elevated',
        className: styles.draftSavedToast,
      })
      lastDraftToastSignatureRef.current = draftToastSignature
    }
    previousSaveStatus.current = saveStatus
  }, [addToast, id, insertionData, isDraftListingToastFlow, progressState, realEstateId, saveStatus, t])

  const onNavigate = useCallback(
    async (screen: Screen) => {
      if (localState.currentScreen !== screen) {
        setLocalState((prevState) => ({
          ...prevState,
          currentScreen: screen,
          currentScreenIndex: wizardScreens.findIndex((flowScreen) => flowScreen.screen.id === screen.id),
        }))
        await dispatch(doNavigate(flowInformation.validationSchema))
      }
    },
    [dispatch, flowInformation.validationSchema, localState.currentScreen, wizardScreens]
  )

  useEffect(() => {
    if (isLoading || hasHandledScreenParamRef.current) {
      return
    }

    hasHandledScreenParamRef.current = true
    const screenName = new URLSearchParams(window.location.search).get(SCREEN_TO_SCROLL_SEARCH_PARAM)
    if (!screenName) {
      return
    }

    const targetScreen = wizardScreens.find(
      (wizardScreen) => wizardScreen.screen.name === screenName || wizardScreen.screen.id === screenName
    )?.screen
    if (!targetScreen) {
      return
    }

    const navigateToScreen = async () => {
      // Special handling for objectDetailsCta screen to expand the section before scrolling to it, as it is collapsed by default and scrollIntoView does not work properly on collapsed elements
      if (targetScreen === SCREENS[SCREEN_ADDITIONAL_INFORMATION_CTA]) {
        document.getElementById(`${targetScreen.id}-title`)?.click()
      }
      await onNavigate(targetScreen)
      document.getElementById(targetScreen.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    void navigateToScreen()
  }, [isLoading, onNavigate, wizardScreens])

  const applyInsertionDataUpdate = useApplyInsertionDataUpdate()

  const onChange = useCallback(
    (screenProperty: ScreenProperty, value: OnChangeValue) => {
      applyInsertionDataUpdate((oldInsertionData) =>
        insertionDataWithChangedProperty(oldInsertionData, screenProperty.property, value)
      )
    },
    [applyInsertionDataUpdate]
  )

  const onPreview = () => doValidation('preview') && dispatch(doPreview(insertionData))

  const onSave = async () => {
    if (!doValidation('submit')) {
      return
    }

    const result = await dispatch(doSaveThunk(insertionData, true))
    setDeferredRedirectUrl(result?.deferredRedirectUrl)
  }
  const onSaveForLater = async () => {
    const isValid = doValidation('submit')
    if (isValid) {
      await dispatch(doSaveThunk(insertionData, false))
      return true
    }
    return false
  }

  const doValidation = useCallback(
    (action: 'submit' | 'preview'): boolean => {
      const validationResultLocal = runValidation(
        store.getState().insertionFeature.insertionData,
        store.getState().addressFeature,
        flowInformation.validationSchema
      )
      const hasValidGeoCoordinatesLocal = store.getState().addressFeature.hasValidGeoCoordinates
      const isValid: boolean = checkValidity(validationResultLocal, hasValidGeoCoordinatesLocal)

      const basicInvalidFields = getInvalidFields(validationResultLocal, hasValidGeoCoordinatesLocal)
      const customInvalidFields = getInvalidFields(customValidationResults, hasValidGeoCoordinatesLocal)
      const invalidFields = [...basicInvalidFields, ...customInvalidFields]
      if (invalidFields.length) {
        const property = invalidFields[0]
        document.getElementById(property)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        const mapping = trackingPropertyMapping[camelCase(property) as TrackingPropertyMappingType]
        dispatch(
          doTracking(
            flowInformation,
            `${action}_incomplete_button_click`,
            false,
            mapping ? mapping : convertTrackingParameter(camelCase(property))
          )
        )
      } else if (isValid) {
        return true
      }

      return false
    },
    [store, flowInformation, customValidationResults, dispatch]
  )

  const doResetInsertionData = (clearingUpOf: ScreenProperty[]) => {
    const newInsertionData: ContentStateInterface = _.cloneDeep(store.getState().insertionFeature.insertionData)

    clearingUpOf.forEach((item: ScreenProperty) => {
      item.property && _.set(newInsertionData, item.property, undefined)
    })
    dispatch(validateAndSaveInsertion(newInsertionData))
    resetTouched(clearingUpOf.map((it) => it.property as ContentStateProperty))
  }
  const doCloseStatusMessage = () => dispatch(doHideMessage())
  const doClosePreviewModal = () => dispatch(hidePreviewModal())

  const doCloseMultimediaStatusMessage = () => dispatch(hideMultimediaMessage())

  const insertionEditorElements: InsertionEditorElements = {
    insertionEditorState: {
      insertionData,
      validationResult,
    },
    insertionEditorMethods: {
      onNavigate,
      onChange,
      doResetInsertionData,
      doCloseMultimediaStatusMessage,
    },
  }
  const flowNameFromStore = useAppSelector((state: InitState) => state.flowFeature.flowInformation.name)

  if (isInitializationAborted || !flowNameFromStore || flowNameFromStore !== flowInformation.name) {
    return null
  }

  return (
    <React.Fragment>
      {isLoading ? (
        <div role='status' />
      ) : (
        <React.Fragment>
          <InsertionWizardPage
            flowInformation={flowInformation}
            screen={localState.currentScreenIndex}
            flowScreens={getFlowScreens(wizardScreens)}
            onNavigate={onNavigate}
            onPreview={onPreview}
            onSave={onSave}
            doCloseStatusMessage={doCloseStatusMessage}
            validationMessages={validationResult}
            onSaveForLater={onSaveForLater}
          >
            {wizardScreens.map((component) => (
              <React.Fragment key={`insertion-editor-${flowInformation.name}-flow-${component.screen.id}`}>
                {component.element(getElementProps(insertionEditorElements, component, flowInformation))}
                {isNativeMobileView && component.screen === SCREENS[SCREEN_MULTIMEDIA] && (
                  <InsertionStartInfoSection
                    isWebView={isNativeMobileView}
                    flowMetadata={flowInformation.metaData}
                    shouldShowDraftListingDescription={shouldShowWebViewDraftListingInfo}
                  />
                )}
              </React.Fragment>
            ))}
          </InsertionWizardPage>
          {previewModalIsOpen && (
            <PreviewModalContainer
              data-testid='preview-modal'
              title='previewModal.header'
              onClose={doClosePreviewModal}
            >
              <PreviewModal onClose={doClosePreviewModal} />
            </PreviewModalContainer>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default InsertionEditor
